import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://ns-dashboard-api.kabilah.com';

export const DEFAULT_REPORT_ITEMS = [
  { name: 'Chief Complaint', icon: 'want', contents: '', pinned: false },
  { name: 'Allergies', icon: 'allergies', contents: '', pinned: false },
  { name: 'Hospital Course', icon: 'vitals', contents: '', pinned: false },
  { name: 'Past Medical History', icon: 'history', contents: '', pinned: false },
  { name: 'Social Context', icon: 'social-context', contents: '', pinned: false },
  { name: 'Vitals', icon: 'tests', contents: '', pinned: false },
  { name: 'Mobility/Fall Risk', icon: 'mobility', contents: '', pinned: false },
  { name: 'Neuro', icon: 'neuro', contents: '', pinned: false },
  { name: 'Cardiac', icon: 'cardiac', contents: '', pinned: false },
  { name: 'Respiratory', icon: 'respiratory', contents: '', pinned: false },
  { name: 'GI', icon: 'gi', contents: '', pinned: false },
  { name: 'GU', icon: 'gu', contents: '', pinned: false },
  { name: 'Skin', icon: 'skin', contents: '', pinned: false },
  { name: 'Musculoskeletal', icon: 'muscle', contents: '', pinned: false },
  { name: 'Drains/IV Sites', icon: 'iv-sites', contents: '', pinned: false },
  { name: 'Medications/IV Fluids', icon: 'medications', contents: '', pinned: false },
  { name: 'Labs/Tests', icon: 'labs', contents: '', pinned: false },
  { name: 'Patient Events', icon: 'complaint', contents: '', pinned: false },
  { name: 'Patient Education', icon: 'notes', contents: '', pinned: false },
];

export const PATIENT_FIELDS = [
  { key: 'name', label: 'Patient Name', wide: false },
  { key: 'dateOfBirth', label: 'Date of Birth', wide: false, type: 'date', required: true },
  { key: 'age', label: 'Age', wide: false },
  { key: 'gender', label: 'Gender', wide: false },
  { key: 'room', label: 'Room', wide: false, required: true },
  { key: 'admitted', label: 'Admitted', wide: false, type: 'date' },
  { key: 'code', label: 'Code Status', wide: false },
  { key: 'doctor', label: 'Attending Physician', wide: true },
];

// Every nursing-school-dashboard MRN is 8 digits starting with 1234, so a case's MRN
// (which doubles as its case id) is instantly recognizable as belonging to this dashboard.
export function generateMrn(existingMrns = []) {
  const taken = new Set(existingMrns.filter(Boolean).map(String));
  let candidate;
  do {
    candidate = '1234' + String(Math.floor(Math.random() * 10000)).padStart(4, '0');
  } while (taken.has(candidate));
  return candidate;
}

export function normalizeDraft(data, objectives) {
  return {
    caseName: data.caseName || data.name || '',
    mrn: data.mrn || '',
    objectives: objectives || data.objectives || '',
    patient: {
      name: data.patient?.name || data.name || '',
      dateOfBirth: data.patient?.dateOfBirth || data.dateOfBirth || '',
      age: data.patient?.age || data.age || '',
      gender: data.patient?.gender || data.gender || '',
      room: data.patient?.room || data.room || '',
      admitted: data.patient?.admitted || data.admitted || '',
      code: data.patient?.code || data.code || '',
      doctor: data.patient?.doctor || data.doctor || '',
    },
    reportItems: (data.reportItems && data.reportItems.length > 0)
      ? data.reportItems
      : DEFAULT_REPORT_ITEMS,
    filesProcessed: data.filesProcessed || [],
    source: data.source || 'custom',
    indicatorCodes: Array.isArray(data.indicatorCodes) ? data.indicatorCodes : [],
    status: data.status || 'draft',
    ownerEmail: data.ownerEmail || null,
    collaborators: Array.isArray(data.collaborators) ? data.collaborators : [],
  };
}

export function validateApproveDraft(draft) {
  if (!draft.patient?.room?.trim()) {
    return { room: 'Room number is required.' };
  }
  return null;
}

export function routeApproveErrorMessage(message) {
  if (message && /room/i.test(message)) {
    return { fieldErrors: { room: message } };
  }
  if (message && /mrn/i.test(message)) {
    return { fieldErrors: { mrn: message } };
  }
  return { generalError: message || 'Could not save the case. Please try again.' };
}

export function saveCase(draft) {
  return axios.post(`${BACKEND_URL}/api/cases`, draft);
}

export function updateCase(caseId, draft) {
  return axios.put(`${BACKEND_URL}/api/cases/${caseId}`, draft);
}

export function fetchDrafts(userEmail) {
  return axios.get(`${BACKEND_URL}/api/cases/drafts`, { params: { userEmail } });
}

export function publishCase(caseId) {
  return axios.post(`${BACKEND_URL}/api/cases/${caseId}/publish`);
}

export function unpublishCase(caseId) {
  return axios.post(`${BACKEND_URL}/api/cases/${caseId}/unpublish`);
}

export function addCollaborator(caseId, email) {
  return axios.post(`${BACKEND_URL}/api/cases/${caseId}/collaborators`, { email });
}

export function removeCollaborator(caseId, email) {
  return axios.delete(`${BACKEND_URL}/api/cases/${caseId}/collaborators/${encodeURIComponent(email)}`);
}
